import React, { useState, useEffect, useContext, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Trans, t } from '@lingui/macro'

import IconArrowLeft from '../../../../components/common/icon/IconArrowLeft'
import IconArrowRight from '../../../../components/common/icon/IconArrowRight'
import { ButtonPrimary, ButtonLight } from 'front-end/src/components'
import NewActionHeader from '../new-action-header/NewActionHeader'
import httpApi from '../../../../api/http-api'
import { linkBuilder } from '../../../../../../shared/linkBuilder'
import { navigateTo } from '../../../../app/util/appHistory'
import { EmailTemplate } from '../../../../../../back-end/src/emails/EmailTemplate'
import { CampaignTemplate } from '../../../../../../back-end/src/emails/CampaignTemplate'
import { notification } from '../../../../components/common/notifications/Notifications'
import { ManageCampaignContext } from '../../ManageCampaignPage'

import { CampaignEmailStepCreate } from './CampaignEmailStepCreate'
import validations from '../../../../../../shared/validations'
import {
  CampaignEmailActionInput,
  CampaignEmailActionModelAsEditor,
  CampaignScheduleEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { CampaignActionScheduleStepComponent } from '../campaign-action-schedule-step-component/CampaignActionScheduleStepComponent'
import { CampaignActionEditingSteps } from '../../common'
import {
  useCreateCampaignEmailMutation,
  usePatchCampaignEmailMutation
} from './CampaignEmailComponent.codegen'

import { interpolate } from 'shared/interpolate'
import { PREVIEW_VARS } from 'shared/preview'
import { useLocation } from 'react-router-dom'
import { useDefaultSender } from '../SenderSelect'
import { useMainHeaderOrgQuery } from 'front-end/src/components/mainheader/MainHeader'
import dayjs, { Dayjs } from 'dayjs'
import { getEmailTemplateStyleProps } from '../../../../../../shared/getEmailTemplateStyleProps'

import './CampaignEmailComponent.less'
import {
  getTimeZoneDateFromUTCDate,
  getUTCDateFromTimeZoneDate
} from '../../../manage-events/event-edit/tabs/event-detail/eventDetailUtils'
import { isFirefox } from '../../../../../../shared/helpers'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

export interface ICampaignEmailComponentProps {
  email?: CampaignEmailActionModelAsEditor
}

export interface ICampaignEmailComponentState {
  coverImageFile: File | null
  coverImageBase64: string | null
  coverImageSrc: string | null
  emailSubject: string
  emailBody: string
  buttonLink: string
  buttonLinkValid: boolean | null
  buttonText: string
  step: string
  timezone: string | null
  scheduleType: CampaignScheduleEnum
  relativeSchedule: {
    day: number | null
    time: string | null
  }
  fixedSchedule: { date: Dayjs | null }
  isSaving: boolean
  saveError: Error | null
  automatic_time_of_day: boolean
  respect_business_hours: boolean
  is_for_manager: boolean
  isEditing: boolean
  email_identity_id: number | null
}

export function useIsManagerNotificationPath() {
  const { pathname } = useLocation()
  const isManagerNotification = pathname.endsWith('new-manager-notification')
  return isManagerNotification
}

export const CampaignEmailComponent = (props: ICampaignEmailComponentProps) => {
  const { campaign } = useContext(ManageCampaignContext)
  const defaultSender = useDefaultSender(campaign.id)
  const isManagerNotification = useIsManagerNotificationPath()
  const { email } = props

  const [state, setState] = useState<ICampaignEmailComponentState>({
    coverImageFile: null,
    coverImageBase64: null,
    coverImageSrc: null,
    emailSubject: '',
    emailBody: '',
    buttonLink: '',
    buttonLinkValid: false,
    buttonText: '',
    step: CampaignActionEditingSteps.CREATE,
    timezone: '',
    scheduleType: campaign.schedule_type as CampaignScheduleEnum,
    relativeSchedule: {
      day: null,
      time: null
    },
    fixedSchedule: {
      date: null
    },
    isSaving: false,
    automatic_time_of_day: email?.automatic_time_of_day ?? false,
    respect_business_hours: email?.respect_business_hours ?? false,
    is_for_manager: email?.is_for_manager ?? false,
    saveError: null,
    isEditing: false,
    email_identity_id: null
  })

  useEffect(() => {
    // we are editing
    if (props.email) {
      const { email } = props
      const imageUrl = getImagePreviewUrl(email)
      const timezone = email.timezone ? email.timezone : dayjs.tz.guess()
      onStateUpdate({
        emailSubject: email.subject,
        emailBody: email.text as string,
        coverImageSrc: imageUrl,
        buttonLink: email.button_link ?? '',
        buttonLinkValid:
          typeof email.button_link === 'string' &&
          !!validations.url.exec(email.button_link),
        buttonText: email.button_caption ?? '',
        timezone: timezone,
        scheduleType: campaign.schedule_type as CampaignScheduleEnum,
        relativeSchedule: {
          day: email.send_at_day ?? 1,
          time: email.send_at_time || '10:00:00'
        },
        fixedSchedule: {
          date: email.fixed_send_time
            ? getTimeZoneDateFromUTCDate({
                date: email.fixed_send_time,
                timezone
              })
            : null
        },
        // @ts-expect-error 9/11/2020
        automatic_time_of_day: email.automatic_time_of_day,
        isEditing: true,
        email_identity_id: email.email_identity_id
      })
    }
  }, [props])

  useEffect(() => {
    // prefill default sender for new emails
    if (!props.email) {
      setState((s) => ({ ...s, email_identity_id: defaultSender }))
    }
  }, [defaultSender])

  const onStateUpdate = (
    updatedState: Partial<ICampaignEmailComponentState>
  ) => {
    setState({ ...state, ...updatedState })
  }

  const [createEmailFn] = useCreateCampaignEmailMutation()
  const create = async (input: CampaignEmailActionInput) => {
    try {
      onStateUpdate({ isSaving: true })

      input.is_for_manager = isManagerNotification
      const { data } = await createEmailFn({
        variables: {
          id: campaign.id,
          input
        }
      })
      return data?.admin?.campaign?.createEmail
    } catch (err) {
      onStateUpdate({ saveError: err })
      return null
    }
  }

  const [patchEmailFn] = usePatchCampaignEmailMutation()
  const update = async (input: CampaignEmailActionInput) => {
    if (campaign.id && props.email) {
      try {
        onStateUpdate({ isSaving: true, saveError: null })
        const { data } = await patchEmailFn({
          variables: {
            id: props.email.id,
            campaignId: campaign.id,
            input
          }
        })
        return data?.admin?.campaign?.emailAction
      } catch (err) {
        onStateUpdate({ saveError: err, isSaving: false })
      }
    }
  }

  const onSave = async () => {
    const data: CampaignEmailActionInput = {
      text: state.emailBody,
      subject: state.emailSubject,
      button_caption: state.buttonText,
      button_link: state.buttonLink,
      automatic_time_of_day: state.automatic_time_of_day,
      respect_business_hours: state.respect_business_hours,
      fixed_send_time: null,
      timezone: null,
      send_at_day: null,
      send_at_time: null,
      email_identity_id: state.email_identity_id
    }

    if (state.scheduleType === CampaignScheduleEnum.RELATIVE) {
      data.send_at_day = state.relativeSchedule.day
      data.send_at_time = state.relativeSchedule.time
    } else if (state.scheduleType === CampaignScheduleEnum.FIXED) {
      const timezone = state.timezone || dayjs.tz.guess()
      data.timezone = timezone
      data.fixed_send_time = getUTCDateFromTimeZoneDate({
        date: state.fixedSchedule.date,
        timezone
      })
    }

    const isCreating = state.isEditing === false

    // remove image
    if (!state.coverImageSrc && !state.coverImageFile) {
      data.image = null
    }

    const updatedEmail = isCreating ? await create(data) : await update(data)
    if (!updatedEmail) {
      onStateUpdate({ isSaving: false })
      notification.error(
        isCreating ? t`Error creating email` : t`Error updating email`
      )
      return
    }

    if (state.coverImageFile) {
      try {
        await httpApi.campaign.uploadCampaignEmailImage(
          campaign.id,
          updatedEmail.id,
          state.coverImageFile
        )
      } catch (err) {
        console.error(err)
        notification.error(t`Error uploading image`)
      }
    }

    onStateUpdate({ isSaving: false })
    notification.info(
      isCreating
        ? t`Campaign's email has been created`
        : t`Campaign's email has been updated`
    )

    navigateTo(linkBuilder.campaign(campaign.id))
  }

  const getImagePreviewUrl = (
    email: CampaignEmailActionModelAsEditor
  ): string | null => {
    if (
      email.image &&
      email.image.variants &&
      email.image.variants['1400x500']
    ) {
      return email.image.variants['1400x500']
    } else if (email.image && email.image.original) {
      return email.image.original
    } else {
      return null
    }
  }

  const { step } = state
  return (
    <div className="CampaignEmailComponent">
      {step === CampaignActionEditingSteps.CREATE && (
        <CampaignEmailStepCreate
          {...state}
          email={props.email}
          onStateUpdate={onStateUpdate}
        />
      )}
      {state.step === CampaignActionEditingSteps.PREVIEW && (
        <CampaignEmailStepPreview {...state} onStateUpdate={onStateUpdate} />
      )}
      {step === CampaignActionEditingSteps.SCHEDULE && (
        <CampaignActionScheduleStepComponent
          {...state}
          onStateUpdate={onStateUpdate}
          onSave={onSave}
          actionType={
            isManagerNotification
              ? CampaignActionTypeEnum.MANAGER_NOTIFICATION
              : CampaignActionTypeEnum.EMAIL
          }
          steps={CampaignActionEditingSteps}
        />
      )}
    </div>
  )
}

const CampaignEmailStepPreview = (
  props: ICampaignEmailComponentState & {
    onStateUpdate: (updatedState: Partial<ICampaignEmailComponentState>) => void
  }
) => {
  const org = useMainHeaderOrgQuery()
  const isManagerNotification = useIsManagerNotificationPath()

  props = {
    ...props,
    emailBody: interpolate(props.emailBody, PREVIEW_VARS),
    buttonText: interpolate(props.buttonText, PREVIEW_VARS)
  }

  if (!org) {
    return null
  }

  return (
    <div className="CampaignEmailComponent__preview">
      <NewActionHeader
        title={isManagerNotification ? t`Notify manager` : t`Send email`}
        steps={CampaignActionEditingSteps}
        currentStep={CampaignActionEditingSteps.PREVIEW}
        prevButton={
          <ButtonLight
            onClick={() => {
              props.onStateUpdate({
                step: CampaignActionEditingSteps.CREATE
              })
            }}
            leftIcon={<IconArrowLeft />}
          >
            <Trans>Back</Trans>
          </ButtonLight>
        }
        nextButton={
          <ButtonPrimary
            onClick={() => {
              props.onStateUpdate({
                step: CampaignActionEditingSteps.SCHEDULE
              })
            }}
            rightIcon={<IconArrowRight />}
          >
            <Trans>Schedule</Trans>
          </ButtonPrimary>
        }
      />
      <IFrame width="100%" height="800" style={{ border: 0 }}>
        <EmailTemplate {...getEmailTemplateStyleProps(org)}>
          <CampaignTemplate
            text={props.emailBody}
            coverImageSrc={props.coverImageSrc}
            coverImageBase64={props.coverImageBase64}
            buttonText={props.buttonText}
            buttonLink={props.buttonLink}
          />
        </EmailTemplate>
      </IFrame>
    </div>
  )
}

// use <iframe> so the preview is isolated from current styles
const IFrame = ({ children, ...props }) => {
  const [mountNode, setMountNode] = useState<HTMLElement>()

  const ref = useCallback((node) => {
    setMountNode(node?.contentWindow?.document?.body)
  }, [])

  if (isFirefox) {
    return (
      <iframe
        {...props}
        onLoad={(content) => {
          const iframe = content?.target as HTMLIFrameElement
          if (iframe?.contentWindow?.document?.body) {
            setMountNode(iframe?.contentWindow.document.body)
          }
        }}
      >
        {mountNode && createPortal(children, mountNode)}
      </iframe>
    )
  }

  return (
    <iframe {...props} ref={ref}>
      {mountNode && createPortal(children, mountNode)}
    </iframe>
  )
}
